/**
 * HomeGardenServices - Delivery service stack for Home & Garden listings.
 *
 * Single panel: Delivery & Assembly, with 3 demo providers covering the
 * spectrum of home & garden items:
 *   - White-Glove furniture delivery (2-person team, room-of-choice)
 *   - Flexible man-and-van (AnyVan-style, single-piece or bulk)
 *   - Small-item next-day courier (garden tools, decor, plants)
 *
 * Every card carries a DEMO badge until a real courier signs up. The whole
 * stack respects the same admin demo-visibility rules already applied to the
 * home-garden parent category on /admin/settings.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Truck, Package, Phone, MapPin, Clock, Star, Sparkles, AlertCircle, ShieldCheck } from 'lucide-react';

const DELIVERY_DEMOS_UK = [
  {
    id: 'hg-del-1', name: 'Panther Furniture Delivery',
    type: 'White-Glove 2-Person',
    tagline: 'Sofa, wardrobe, dining set - brought to your room of choice',
    rating: 4.7, reviews: 3210, phone: '0330 002 0800',
    coverage: 'UK mainland, book a 2-hr slot',
    tags: ['Room of Choice', 'Assembly', 'Old Item Removal'],
    price_from: 45, eta: 'Next-day / 2-day',
  },
  {
    id: 'hg-del-2', name: 'AnyVan - Man & Van',
    type: 'Flexible Courier',
    tagline: 'Single-piece or full-house move - compare instant quotes',
    rating: 4.8, reviews: 12400, phone: '020 3872 8100',
    coverage: 'UK-wide, self-pick slot',
    tags: ['Instant Quote', 'Insurance Included', 'Same-Day'],
    price_from: 25, eta: 'Same-day possible',
  },
  {
    id: 'hg-del-3', name: 'DPD Local - Small Items',
    type: 'Standard Courier',
    tagline: 'Plants, tools, decor, lamps - tracked to the hour',
    rating: 4.8, reviews: 5240, phone: '0121 275 0500',
    coverage: 'UK-wide, next-day',
    tags: ['Next-Day', '1-hr Slot', 'Photo Proof'],
    price_from: 6.5, eta: 'Next-day',
  },
];

const DELIVERY_DEMOS_SL = [
  {
    id: 'hg-del-1-sl', name: 'Kapruka Home Delivery',
    type: 'White-Glove 2-Person',
    tagline: 'Sofa, wardrobe, dining set - brought to your room of choice',
    rating: 4.6, reviews: 2140, phone: '011 483 6436',
    coverage: 'Islandwide, book a 2-hr slot',
    tags: ['Room of Choice', 'Assembly', 'Old Item Removal'],
    price_from: 5500, eta: 'Next-day / 2-day',
  },
  {
    id: 'hg-del-2-sl', name: 'PickMe Flash - Man & Van',
    type: 'Flexible Courier',
    tagline: 'Single-piece or full-house move - book on the app',
    rating: 4.7, reviews: 6820, phone: '011 481 5111',
    coverage: 'Colombo + suburbs, self-pick slot',
    tags: ['Instant Quote', 'Insurance Included', 'Same-Day'],
    price_from: 2500, eta: 'Same-day possible',
  },
  {
    id: 'hg-del-3-sl', name: 'Pronto Lanka - Small Items',
    type: 'Standard Courier',
    tagline: 'Plants, tools, decor, lamps - tracked to the hour',
    rating: 4.6, reviews: 1820, phone: '011 231 8888',
    coverage: 'Islandwide, next-day',
    tags: ['Next-Day', 'Cash on Delivery', 'Photo Proof'],
    price_from: 550, eta: 'Next-day',
  },
];

const ProviderCard = ({ provider, isDemo, testIdPrefix, currencyPrefix = '£' }) => (
  <div
    className="relative border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
    data-testid={`${testIdPrefix}-${provider.id}`}
  >
    {isDemo && (
      <div
        className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl"
        data-testid={`${testIdPrefix}-demo-${provider.id}`}
      >
        DEMO
      </div>
    )}
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-green-50 border-green-200 text-green-600">
        <Truck className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h4 className="font-semibold text-gray-900 text-sm">{provider.name}</h4>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{provider.type}</Badge>
        </div>
        <p className="text-xs text-gray-500 italic mt-0.5">{provider.tagline}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <span className="flex items-center gap-0.5 text-amber-600">
            <Star className="h-3 w-3 fill-current" />
            {provider.rating.toFixed(1)}
          </span>
          <span>({provider.reviews.toLocaleString()} reviews)</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {provider.coverage}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {provider.eta}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {provider.tags.map((tag) => (
            <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold text-emerald-700">
            From {currencyPrefix}{provider.price_from.toLocaleString()}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => window.open(`tel:${provider.phone.replace(/\s/g, '')}`, '_self')}
            data-testid={`${testIdPrefix}-call-${provider.id}`}
          >
            <Phone className="h-3 w-3 mr-1" />
            Call
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const HomeGardenServices = ({ listing }) => {
  const isDemo = !!listing?.is_demo;
  const country = (listing?.country || '').toUpperCase();
  const isSL = country === 'LK' || country === 'SL';
  const deliveryDemos = isSL ? DELIVERY_DEMOS_SL : DELIVERY_DEMOS_UK;
  const currencyPrefix = isSL ? 'Rs. ' : '£';

  return (
    <div className="space-y-6" data-testid="home-garden-services-stack">
      {isDemo && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="home-garden-services-demo-notice">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <Badge variant="secondary" className="mr-2 bg-amber-100 text-amber-800 border-amber-200">DEMO</Badge>
            Delivery options below are illustrative for this demo listing. Live couriers appear on real seller listings.
          </AlertDescription>
        </Alert>
      )}

      <Card data-testid="home-garden-delivery-section">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Truck className="h-5 w-5 text-green-600" />
            <CardTitle className="text-lg">Delivery &amp; Assembly</CardTitle>
            <Badge variant="outline" className="text-xs">Courier partners</Badge>
            {isDemo && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="delivery-demo-badge">DEMO</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            From single plants to full sofas - pick the right courier for {listing?.title || 'your item'}.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {deliveryDemos.map((p) => (
              <ProviderCard key={p.id} provider={p} isDemo={true} testIdPrefix="hg-delivery-provider" currencyPrefix={currencyPrefix} />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>All partners carry goods-in-transit insurance - item damage during delivery is covered.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeGardenServices;
